<template>
  <b-card>
    <validation-observer ref="permissionsDetailForm">
      <div>
        <b-button
          id="default-filter-new-btn"
          variant="primary"
          type="button"
          size="sm"
          class="mb-2"
          @click="openPermissionsModal"
        >
          <feather-icon icon="PlusIcon" size="15" />
          <b-tooltip
            triggers="hover"
            target="default-filter-new-btn"
            :title="t('Add New Permission')"
            :delay="{ show: 0, hide: 50 }"
          />
        </b-button>
        <vue-good-table
          mode="remote"
          :sort-options="{ enabled: false }"
          :is-loading.sync="isLoading"
          :show-footer="true"
          :pagination-options="{
            enabled: true,
            jumpFirstOrLast: true,
            rowsPerPageLabel: t('Rows per page'),
            ofLabel: t('ofLabel'),
            nextLabel: t('nextLabel'),
            prevLabel: t('prevLabel'),
            dropdownAllowAll: false
          }"
          :rows="rows"
          :columns="getColumns"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'name'">
              {{ props.row.name || '-' }}
            </span>
            <span v-if="props.column.field === 'description'">
              {{ props.row.description }}
            </span>
            <span v-else-if="props.column.field === 'status'">
              <b-badge pill :variant="props.row.status ? 'success' : 'danger'">
                {{ props.row.status ? t('Active') : t('Passive') }}
              </b-badge>
            </span>

            <span v-else-if="props.column.field === 'actions'">
              <span class="d-flex justify-content-center table-icon-buttons">
                <b-button
                  :id="`delete-permissions-button-${props.row.id}`"
                  variant="danger"
                  type="button"
                  size="sm"
                  @click="getDeletePermissions(props.row.id)"
                >
                  <feather-icon icon="XIcon" size="15" />
                  <b-tooltip
                    triggers="hover"
                    :target="`delete-permissions-button-${props.row.id}`"
                    :title="t('Delete Permissions')"
                    :delay="{ show: 0, hide: 0 }"
                    boundary="viewport"
                  />
                </b-button>
              </span>
            </span>
          </template>
        </vue-good-table>
      </div>
      <b-modal
        id="permissionsModal"
        ref="permissionsModal"
        title="İzin Seç"
        size="lg"
        :ok-title="t('Save')"
        :cancel-title="t('Close')"
        cancel-variant="outline-secondary"
        multiple
        @ok="handleOk"
      >
        <div v-if="filteredPermissions.length > 0">
          <vue-good-table
            mode="local"
            :columns="getColumns"
            :rows="filteredPermissions"
            :sort-options="{ enabled: false }"
            :is-loading.sync="isLoading"
            :show-footer="true"
            :pagination-options="{
              enabled: true,
              jumpFirstOrLast: true,
              rowsPerPageLabel: t('Rows per page'),
              ofLabel: t('ofLabel'),
              nextLabel: t('nextLabel'),
              prevLabel: t('prevLabel'),
              dropdownAllowAll: false
            }"
            :search-options="{ enabled: true, placeholder: 'İzinleri Ara' }"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange"
          >
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'description'">
                {{ props.row.description }}
              </span>
              <span
                v-else-if="props.column.field === 'actions'"
                class="d-flex justify-content-center"
              >
                <b-button
                  variant="outline-primary"
                  :class="{
                    'default-filter-new-btn': !isChecked(props.row),
                    'btn-danger': isChecked(props.row)
                  }"
                  @click="selectPermissions(props.row)"
                >
                  <feather-icon v-if="isChecked(props.row)" icon="XIcon" size="15" />
                  <feather-icon v-else icon="PlusIcon" size="15" />
                </b-button>
              </span>
            </template>
          </vue-good-table>
        </div>
        <div v-else>
          <p class="text-center m-1">
            {{ t('All permissions have been granted') }}
          </p>
        </div>
        <div v-if="checkedPermissions.length > 0" class="py-2">
          {{ t('Number of permissions selected:') }} {{ checkedPermissions.length }}
        </div>
      </b-modal>
    </validation-observer>
  </b-card>
</template>
<script>
import { BBadge, BButton, BCard, BTooltip, BModal } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { ValidationObserver } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import useService from '@/auth/service/useService';

export default {
  components: {
    ValidationObserver,
    BBadge,
    BButton,
    BCard,
    BTooltip,
    BModal,
    VueGoodTable
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      filter: {
        page: 1,
        limit: 10
      },
      values: {
        status: false
      },
      checkedPermissions: []
    };
  },

  computed: {
    ...mapGetters({
      allRoles: 'app/allRoles',
      allPermissions: 'app/allPermissions'
    }),
    getColumns() {
      return [
        {
          label: this.$t('Description'),
          field: 'description'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    },
    filteredPermissions() {
      const existingPermissionIds = this.rows.map(row => row.id);
      const filteredPermissions = this.allPermissions.filter(
        permission => !existingPermissionIds.includes(permission.id)
      );
      return filteredPermissions;
    }
  },

  async created() {
    this.loadItems();
  },

  methods: {
    getPermissionsList() {
      useService.getPermissions({ page: 1, limit: 1000 }).then(response => {
        this.$store.commit('app/SET_ALL_PERMISSIONS', response.data);
      });
    },
    selectPermissions(permissions) {
      const index = this.checkedPermissions.findIndex(p => p.id === permissions.id);

      if (index === -1) {
        this.checkedPermissions.push(permissions);
      } else {
        this.checkedPermissions.splice(index, 1);
      }
    },
    isChecked(permissions) {
      return this.checkedPermissions.some(p => p.id === permissions.id);
    },
    openPermissionsModal() {
      this.$bvModal.show('permissionsModal');
      this.checkedPermissions = [];
    },
    handleOk() {
      const roleId = this.$route.params.id;
      const permissionsId = this.checkedPermissions.map(x => x.id);

      const existingPermissions = this.rows.map(row => row.id);

      const mergedPermissions = Array.from(new Set([...existingPermissions, ...permissionsId]));

      const permissionsData = {
        permissions: mergedPermissions
      };

      useService
        .addRolePermissions(roleId, permissionsData)
        .then(response => {
          this.showSuccessMessage(`${response.message}`);
        })
        .catch(messages => {
          if (Array.isArray(messages) && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.loadItems();
        });

      this.$bvModal.hide('permissionsModal');
    },

    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },
    deletePermission(permissionsId, id) {
      return useService.deletePermission(permissionsId, id);
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      const { id } = this.$route.params;

      // let requestData = this.filter;

      this.isLoading = true;

      useService
        .getRolePermissions(id)
        .then(response => {
          this.rows = response.data;
        })
        .finally(() => {
          this.isLoading = false;
        });

      if (this.allRoles.length < 1) {
        this.getPermissionsList(id);
      }
    },
    getDeletePermissions(permissionsId) {
      const { id } = this.$route.params;

      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Permissions Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.deletePermission(permissionsId, id)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Price Package Was Deleted')}`
              );

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table';

.package-card {
  background-color: #4f4f4f80 !important;
  margin: 24px;
}

.checkbox {
  width: 100%;
  margin-left: 5px;
}

.permissions-list {
  background-color: transparent;
  padding: 15px;
}

.permissions-item {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid grey;
  border-radius: 7px;
  padding: 10px 10px;
  width: 100%;
}

.permissions-info {
  display: flex;
  align-items: center;
}

.permissions-name {
  flex: 1;
  font-weight: bold;
  font-size: 12px;
  white-space: break-spaces;
}

.permissions-item .b-btn-outline-primary {
  width: 70px;
  font-size: 11px;
  display: flex;
  justify-content: center;
}
</style>
